import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Returning from Google OAuth: let the callback exchange the auth code first.
    if (window.location.pathname === "/auth/google" && window.location.search.includes("code=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("kiosk_admin_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Bootstrap the session. IMPORTANT: only treat an explicit 401/403 as logged-out.
    // Transient 5xx / network / timeout (e.g. the backend warming up right after a redeploy)
    // must NOT clear the token or force a re-login — retry with backoff instead.
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          const res = await api.get("/auth/me");
          if (!cancelled) { setUser(res.data); setLoading(false); }
          return;
        } catch (err) {
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem("kiosk_admin_token");
            if (!cancelled) { setUser(false); setLoading(false); }
            return;
          }
          // transient — keep the token and retry
          await new Promise((r) => setTimeout(r, Math.min(800 * 2 ** attempt, 6000)));
        }
      }
      // Couldn't reach the backend, but the token is untouched: a later reload restores the session.
      if (!cancelled) { setUser(false); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const trust_token = localStorage.getItem("hv_2fa_trust") || undefined;
    const res = await api.post("/auth/login", { email, password, trust_token });
    if (res.data.twofa_required) return res.data;
    localStorage.setItem("kiosk_admin_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("kiosk_admin_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
